import type { Step } from 'react-joyride';
import type { TFunction } from 'i18next';
import { getTourDefinition } from './registry';
import { scrollTourTargetIntoView } from './scrollTourTarget';
import type { TourId } from './types';

/** Brief pause so tab panels can mount after `espotz-tour:before-step`. */
const BEFORE_SCROLL_WAIT_MS = 50;

const waitMs = (ms: number) =>
  new Promise<void>(resolve => {
    window.setTimeout(resolve, ms);
  });

export const buildTourSteps = (
  tourId: TourId,
  t: TFunction<'joyride'>,
): Step[] => {
  const definition = getTourDefinition(tourId);

  return definition.steps.map(step => {
    const placement = step.placement ?? 'bottom';
    const isCentered = placement === 'center';

    return {
      target: step.target,
      title: t(step.titleKey),
      content: t(step.contentKey),
      placement,
      skipBeacon: step.skipBeacon ?? false,
      // Always skip Joyride's built-in scroll; we scroll in `before` instead.
      skipScroll: true,
      ...(step.offset !== undefined ? { offset: step.offset } : {}),
      ...(step.spotlightTarget !== undefined
        ? { spotlightTarget: step.spotlightTarget }
        : {}),
      ...(step.spotlightPadding !== undefined
        ? { spotlightPadding: step.spotlightPadding }
        : {}),
      ...(step.disableFlip || step.floatingOptions
        ? {
            floatingOptions: {
              ...step.floatingOptions,
              ...(step.disableFlip ? { flipOptions: false as const } : {}),
            },
          }
        : {}),
      before: async () => {
        window.dispatchEvent(
          new CustomEvent('espotz-tour:before-step', {
            detail: { target: step.target, switchTab: step.switchTab },
          }),
        );
        if (!isCentered) {
          // Tab/step switches (and remounts like Fixed pool) need a beat to mount.
          await waitMs(
            step.switchTab ||
              step.target.includes('entry-fee-config') ||
              step.target.includes('entry-fee-commission') ||
              step.target.includes('create-tournament-basic-info') ||
              step.target.includes('structure-stages')
              ? 150
              : BEFORE_SCROLL_WAIT_MS,
          );
          await scrollTourTargetIntoView(step.spotlightTarget ?? step.target, {
            placement,
            topScrollRoom: step.topScrollRoom,
          });
        }
      },
    };
  });
};
